import CallOutlinedIcon from "@mui/icons-material/CallOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, ToggleButton, ToggleButtonGroup, Tooltip } from "@mui/material";
import { useEffect, useId, useRef, useState } from "react";
import { fetchOssOrderDetail, getOssDocumentDownloadUrl } from "../../api/ossApi";
import { actionStatus, displayLabel } from "../../constants/labels";
import { OssStatusChip } from "../OssStatusChip";
import { OssActionHistoryPanel } from "./OssActionHistoryPanel";
import { useActionDraft } from "./useActionDraft";
import "./OssActionProcessDialog.css";

const ActionField = ({ label, multiline, onChange, ...props }) => {
  const id = useId();
  return (
    <div className="oss-action-field">
      <label htmlFor={id}>{label}</label>
      {multiline ? <textarea id={id} rows={2} {...props} onChange={onChange} /> :
        <input id={id} {...props} onChange={onChange}
          onInput={props.type === "date" || props.type === "datetime-local" ? onChange : undefined} />}
    </div>
  );
};

const InfoCell = ({ label, value }) => (
  <div className="oss-action-info">
    <dt>{label}</dt><dd>{value ?? "-"}</dd>
  </div>
);

const documentTypes = { PURCHASE_ORDER: "발주서", ORDER_FORM: "주문서", CHANGE_ORDER: "변경 근거", SPEC: "규격서", DRAWING: "도면", CONTRACT: "계약서", SPECIFICATION: "사양서", OTHER: "기타 문서" };

const formatFileSize = (value) => {
  const size = Number(value || 0);
  if (!size) return "-";
  if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  if (size >= 1024) return `${Math.round(size / 1024)} KB`;
  return `${size} B`;
};

const buildActionNote = ({
  mode,
  actionAt,
  contactTarget,
  contactChannel,
  actionReason,
  checkedContent,
  actionPlan,
  nextPlan,
  memo,
  item,
}) => {
  const prefix =
    mode === "NOTE"
      ? "[기록 저장]"
      : mode === "FOLLOW_UP"
        ? "[다시 확인 예약]"
        : "[완료 처리]";
  const lines = [
    `${prefix} ${item?.exception_title || "조치 필요"} 확인`,
    actionAt ? `조치 일시: ${actionAt.replace("T", " ")}` : "",
    contactTarget ? `확인 대상: ${contactTarget}` : "",
    contactChannel ? `확인 경로: ${contactChannel}` : "",
    actionReason ? `확인 사유: ${actionReason}` : "",
    checkedContent ? `확인 내용: ${checkedContent}` : "",
    actionPlan ? `조치 내용: ${actionPlan}` : "",
    nextPlan ? `후속 계획: ${nextPlan}` : "",
    memo ? `비고/메모: ${memo}` : "",
  ].filter(Boolean);
  return lines.join("\n");
};

const getTargetLine = (detail, item) =>
  (detail?.lines || []).find((line) => String(line.order_line_id) === String(item?.order_line_id)) || null;

const modeOptions = [
  {
    value: "NOTE",
    label: "기록만 저장",
    helper: "기록만 추가하며, 처리 상태와 다음 확인일은 변경하지 않습니다.",
  },
  {
    value: "DONE",
    label: "완료 처리",
    helper: "확인 결과를 남기고 조치 필요 목록에서 제외합니다.",
  },
  {
    value: "FOLLOW_UP",
    label: "다시 확인 예약",
      helper: "확인 내용을 저장하고, 선택한 날짜에 다시 확인하도록 예약합니다.",
  },
];

const historyText = (value) => {
  if (!value) return "-";
  const labels = {
    ORDER: "주문 상태",
    SCHEDULE: "일정 상태",
    ACTION: "조치 기록",
    RECEIVED: "주문 접수",
    WAITING: "일정 대기",
    CONFIRMED: "계획 확정",
    CHANGE_REVIEW: "변경 검토",
    CHECK_REQUIRED: "확인 필요",
    PLAN_REVIEW: "계획 검토",
    IN_PROGRESS: "재확인 예정",
    DONE: "처리 완료",
  };
  return String(value)
    .split(/(ORDER|SCHEDULE|ACTION|RECEIVED|WAITING|CONFIRMED|CHANGE_REVIEW|CHECK_REQUIRED|PLAN_REVIEW|IN_PROGRESS|DONE)/g)
    .map((part) => labels[part] || part)
    .join("");
};

export const OssActionProcessDialog = ({
  open,
  item,
  groupCode,
  projectCode,
  loading,
  onClose,
  onSubmit,
}) => {
  const draft = useActionDraft(open, `oss-action-draft:${groupCode}:${projectCode}:${item?.order_id}:${item?.order_line_id}`,
    item?.exception_message || item?.exception_title || "");
  const { memo, actionAt, contactTarget, contactChannel, actionReason, checkedContent, actionPlan, nextPlan, mode, nextCheckDate, recordScope, setField } = draft;
  const setMemo = (value) => setField("memo", value);
  const setActionAt = (value) => setField("actionAt", value);
  const setContactTarget = (value) => setField("contactTarget", value);
  const setContactChannel = (value) => setField("contactChannel", value);
  const setActionReason = (value) => setField("actionReason", value);
  const setCheckedContent = (value) => setField("checkedContent", value);
  const setActionPlan = (value) => setField("actionPlan", value);
  const setNextPlan = (value) => setField("nextPlan", value);
  const setMode = (value) => setField("mode", value);
  const setNextCheckDate = (value) => setField("nextCheckDate", value);
  const [closeConfirm, setCloseConfirm] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [savedMessage, setSavedMessage] = useState("");
  const [refresh, setRefresh] = useState(0);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const busy = loading || saving;
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");

  useEffect(() => { setDetail(null); }, [open, item?.order_id, groupCode, projectCode]);

  useEffect(() => {
    if (!open) return;
    setCloseConfirm(false);
    setSubmitError("");
    setSavedMessage("");
  }, [open, item?.order_line_id]);

  useEffect(() => {
    let alive = true;
    const loadDetail = async () => {
      if (!open || !item?.order_id || !groupCode || !projectCode) return;
      setDetailLoading(true);
      setDetailError("");
      try {
        const data = await fetchOssOrderDetail(groupCode, projectCode, item.order_id);
        if (alive) setDetail(data);
      } catch (error) {
        if (alive) {
          setDetail(null);
          setDetailError(error.response?.data?.message || "주문 상세 정보를 불러오지 못했습니다.");
        }
      } finally {
        if (alive) setDetailLoading(false);
      }
    };
    loadDetail();
    return () => {
      alive = false;
    };
  }, [groupCode, item?.order_id, open, projectCode, refresh]);

  if (!item) return null;

  const isFollowUp = mode === "FOLLOW_UP";
  const isNoteOnly = mode === "NOTE";
  const hasActionEntry = [
    checkedContent,
    actionPlan,
    nextPlan,
    memo,
  ].some((value) => String(value || "").trim());
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowValue = new Date(tomorrow.getTime() - tomorrow.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  const validationMessage = !actionAt ? "조치 일시를 입력해 주세요." :
    !hasActionEntry ? "확인 내용, 조치 내용, 후속 계획 또는 메모 중 하나를 입력해 주세요." :
      isFollowUp && !nextCheckDate ? "다음 확인일을 선택해 주세요." :
        isFollowUp && nextCheckDate < tomorrowValue ? "다음 확인일은 내일 이후로 선택해 주세요." : "";
  const submitDisabled = busy || Boolean(validationMessage);
  const targetLine = getTargetLine(detail, item);
  const documents = detail?.documents || [];
  const histories = detail?.histories || [];
  const actions = detail?.actions || [];
  // Action rows include the contact fields and follow-up date omitted by older history responses.
  const historyRows = (actions.length ? [
    ...actions.map((action) => ({ ...action, history_type: "ACTION" })),
    ...histories.filter((history) => history.history_type === "STATUS"),
  ] : [...histories]).sort((a, b) => String(b.occurred_at || b.action_at || "").localeCompare(String(a.occurred_at || a.action_at || "")));

  const orderInfo = detail?.order || {};
  const targetSummary = targetLine || item;
  const composedNote = buildActionNote({
    mode,
    actionAt,
    contactTarget,
    contactChannel,
    actionReason,
    checkedContent,
    actionPlan,
    nextPlan,
    memo,
    item,
  });

  const requestClose = () => {
    if (busy) return;
    if (draft.dirty) setCloseConfirm(true);
    else onClose();
  };
  const submit = async () => {
    if (submitDisabled || savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setSubmitError("");
    setSavedMessage("");
    try {
      const result = await onSubmit({
        recordScope,
        actionStatus: isFollowUp ? "IN_PROGRESS" : isNoteOnly ? "NOTE" : "DONE",
        actionRecord: { actionAt, contactTarget, contactChannel, actionReason, checkedContent, actionPlan, nextPlan, memo },
        resultNote: composedNote,
        nextCheckDate: isFollowUp ? nextCheckDate : undefined,
        checkResult: isFollowUp ? "UNDECIDED" : "ON_SCHEDULE",
      });
      if (result === false) throw new Error("저장하지 못했습니다. 입력 내용을 확인한 후 다시 시도해 주세요.");
      draft.reset();
      setSavedMessage("기록을 저장했습니다.");
      setRefresh((value) => value + 1);
    } catch (error) {
      setSubmitError(error.response?.data?.message || error.message || "저장하지 못했습니다. 다시 시도해 주세요.");
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={requestClose} maxWidth={false}
      aria-labelledby="oss-action-dialog-title"
      PaperProps={{ className: "oss-action-dialog oss-action-process-dialog" }}>
      <header className="oss-action-header">
        <span className="oss-action-heading-icon"><CallOutlinedIcon /></span>
        <div className="oss-action-heading">
          <h2 id="oss-action-dialog-title">조치 처리</h2>
          <p>{item.order_no} · {item.customer_name || "-"}</p>
        </div>
        <Tooltip title="닫기"><IconButton aria-label="조치 처리 닫기" onClick={requestClose} disabled={busy}><CloseRoundedIcon /></IconButton></Tooltip>
      </header>

      <div className="oss-action-body">
        <section className="oss-action-summary" aria-label="처리 대상 및 주문 정보">
          <div className="oss-action-target">
            <div>
              <h3>{item.item_name_snapshot || "주문 품목"}</h3>
              <p><strong>{item.exception_title || "확인 필요"}</strong> · {item.exception_message || "주문 일정을 확인해 주세요."}</p>
            </div>
            <div className="oss-action-chips">
              <OssStatusChip value={targetSummary.schedule_status} /><OssStatusChip type="risk" value={targetSummary.risk_level} />
            </div>
          </div>
          <dl className="oss-action-summary-grid">
            <InfoCell label="고객 발주번호" value={orderInfo.customer_po_no || "-"} />
            <InfoCell label="납품처 / 현장" value={item.site_name || orderInfo.site_name || "-"} />
            <InfoCell label="요청 납기" value={item.requested_due_date} />
            <InfoCell label="착수 필요일" value={targetSummary.required_start_date} />
            <InfoCell label="다음 확인일" value={targetSummary.next_check_date || targetSummary.check_required_date} />
            <InfoCell label="착수 기준 여유일" value={targetSummary.available_margin_days == null ? "-" : `${targetSummary.available_margin_days}일`} />
          </dl>
        </section>

        <div className="oss-action-workspace">
          <section className="oss-action-editor" aria-labelledby="oss-action-record-title">
            <div className="oss-action-section-heading"><CallOutlinedIcon /><h3 id="oss-action-record-title">조치 기록</h3>
              <div className="oss-action-field oss-action-scope">
                <label htmlFor="oss-action-scope">기록 대상</label>
                <select id="oss-action-scope" value={recordScope} disabled={busy} onChange={(event) => {
                  setField("recordScope", event.target.value);
                  if (event.target.value === "ORDER") setMode("NOTE");
                }}>
                  <option value="LINE">품목 · {item.item_name_snapshot || "선택한 품목"}</option>
                  <option value="ORDER">주문 공통</option>
                </select>
              </div>
            </div>
            <div className="oss-action-editor-scroll">
              <ToggleButtonGroup exclusive value={mode} aria-label="처리 방식" className="oss-action-modes"
                onChange={(_, value) => { if (value) setMode(value); }} disabled={busy}>
                {modeOptions.map((option) => <ToggleButton key={option.value} value={option.value} disabled={busy || (recordScope === "ORDER" && option.value !== "NOTE")}>{option.label}</ToggleButton>)}
              </ToggleButtonGroup>
              <p className="oss-action-mode-help">{modeOptions.find((option) => option.value === mode)?.helper}</p>
              <fieldset className="oss-action-fields" disabled={busy}>
                <div className={`oss-action-record-meta${isFollowUp ? " is-followup" : ""}`}>
                  <ActionField label="조치 일시" type="datetime-local" value={actionAt} onChange={(event) => setActionAt(event.target.value)} />
                  {isFollowUp && <ActionField label="다음 확인일" type="date" min={tomorrowValue} required title="내일 이후 날짜를 선택해 주세요." value={nextCheckDate} onChange={(event) => setNextCheckDate(event.target.value)} />}
                  <ActionField label="확인 대상" value={contactTarget} onChange={(event) => setContactTarget(event.target.value)} placeholder="담당자 또는 부서" />
                  <ActionField label="확인 경로" value={contactChannel} onChange={(event) => setContactChannel(event.target.value)} placeholder="전화, 메일, 메신저" />
                  <ActionField label="확인 사유" value={actionReason} onChange={(event) => setActionReason(event.target.value)} />
                </div>
                <ActionField label="확인 내용" multiline value={checkedContent} onChange={(event) => setCheckedContent(event.target.value)} placeholder="확인한 사실과 답변" />
                <ActionField label="조치 내용" multiline value={actionPlan} onChange={(event) => setActionPlan(event.target.value)} placeholder="처리한 내용" />
                <ActionField label="후속 계획" multiline value={nextPlan} onChange={(event) => setNextPlan(event.target.value)} placeholder="이후 진행할 사항" />
                <ActionField label="비고 / 메모" multiline value={memo} onChange={(event) => setMemo(event.target.value)} placeholder="추가 참고사항 (선택)" />
              </fieldset>
            </div>
          </section>

          <aside className="oss-action-reference" aria-label="이력 및 문서">
            {detailError ? <Alert severity="warning">{detailError}</Alert> : null}
            <OssActionHistoryPanel key={`${open}-${item.order_id}-${item.order_line_id}`}
              rows={historyRows} loading={detailLoading} error={detailError} lines={detail?.lines}
              formatTitle={historyText}
              formatStatus={(row) => historyText(displayLabel(actionStatus, row.action_status || row.description))} />
            <section className="oss-action-documents" aria-labelledby="oss-action-documents-title">
              <div className="oss-action-section-heading"><DescriptionOutlinedIcon /><h3 id="oss-action-documents-title">문서 / 발주서</h3><span>{documents.length}건</span></div>
              <div className="oss-action-list" tabIndex={0} aria-label="문서 목록">
                {detailLoading ? <div className="oss-action-empty"><CircularProgress size={22} aria-label="문서 불러오는 중" /></div> :
                  documents.length ? documents.map((document) => (
                    <article className="oss-action-document-entry" key={document.document_id}>
                      <DescriptionOutlinedIcon className="oss-action-file-icon" />
                      <div>
                        <p className="oss-action-file-name">{document.file_name}</p>
                        <p className="oss-action-file-meta">{documentTypes[document.document_type] || "문서"} · {formatFileSize(document.file_size)}</p>
                      </div>
                      <Tooltip title={`${document.file_name} 새 창에서 열기`}>
                        <IconButton aria-label={`${document.file_name} 열기`} onClick={() => window.open(getOssDocumentDownloadUrl(groupCode, projectCode, document.document_id), "_blank", "noopener,noreferrer")}><OpenInNewRoundedIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    </article>
                  )) : <p className="oss-action-empty">{detailError ? "문서를 불러오지 못했습니다." : "등록된 문서가 없습니다."}</p>}
              </div>
            </section>
          </aside>
        </div>
      </div>
      <footer className="oss-action-footer">
        <div className="oss-action-save-status" role={submitError || draft.storageError ? "alert" : "status"}>
          {submitError || (draft.storageError ? "임시 보관을 사용할 수 없습니다. 닫기 전에 저장해 주세요." : draft.dirty ? (draft.restored ? "작성 중이던 기록을 복원했습니다." : "미저장 · 이 탭에 임시 보관됨") : savedMessage)}
        </div>
        <Button onClick={requestClose} color="inherit" disabled={busy}>닫기</Button>
        {validationMessage && draft.dirty && <span className="oss-action-validation" id="oss-action-validation">{validationMessage}</span>}
        <Button variant="contained" disabled={submitDisabled} aria-describedby={validationMessage && draft.dirty ? "oss-action-validation" : undefined}
          onClick={submit}>
          {busy ? "저장 중..." : isFollowUp ? "다시 확인 예약" : isNoteOnly ? "기록 저장" : "완료 처리"}
        </Button>
      </footer>
      <Dialog open={closeConfirm} onClose={() => setCloseConfirm(false)} aria-labelledby="oss-action-close-title" maxWidth="xs" fullWidth>
        <DialogTitle id="oss-action-close-title">작성 중인 기록이 있습니다</DialogTitle>
        <DialogContent>{draft.storageError ? "아직 저장하지 않았습니다. 닫으면 작성 내용이 사라질 수 있습니다." : "서버에 저장하지 않은 내용은 이 탭에 8시간 동안 임시 보관됩니다. 모달을 닫을까요?"}</DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 1, flexWrap: "wrap", gap: 1 }}>
          <Button onClick={() => { draft.reset(); setCloseConfirm(false); onClose(); }} color="error">내용 버리기</Button>
          <Button onClick={() => setCloseConfirm(false)}>계속 작성</Button>
          {!draft.storageError && <Button onClick={() => { setCloseConfirm(false); onClose(); }}>임시 보관 후 닫기</Button>}
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};
